import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "geovinum_token";
const USER_KEY = "geovinum_user";

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  setToken: (t) => localStorage.setItem(TOKEN_KEY, t),
  clearToken: () => localStorage.removeItem(TOKEN_KEY),
  getUser: () => {
    const raw = localStorage.getItem(USER_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  },
  setUser: (u) => localStorage.setItem(USER_KEY, JSON.stringify(u)),
  clearUser: () => localStorage.removeItem(USER_KEY),
  logout: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); },
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const t = auth.getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Global response interceptor: when the API signals insufficient credits or a
// locked Pro/Amateur feature, broadcast a custom event so the app can surface
// a contextual UpgradeNudge modal (handled by <UpgradeNudgeListener />).
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.error : null;
    // Only act on actual error responses (>=400). Never on 200 even if body looks weird.
    if (status >= 400) {
      // 401 with stale token → clear localStorage & redirect to login. This
      // prevents the "Invalid token" toast from showing across the app when
      // the JWT has expired or the server secret has rotated.
      if (status === 401 && typeof detail === "string" &&
          /invalid token|user not found|missing credentials|expired/i.test(detail)) {
        try {
          localStorage.removeItem("geovinum_token");
          localStorage.removeItem("geovinum_user");
        } catch { /* ignore */ }
        // Avoid redirect loops on the login/register pages themselves
        const p = window.location.pathname;
        if (!/^\/(login|register|checkout\/complete)/.test(p)) {
          const next = encodeURIComponent(p + window.location.search);
          window.location.href = `/login?next=${next}&reason=expired`;
        }
      }
      if (status === 402 || code === "insufficient_credits") {
        window.dispatchEvent(new CustomEvent("geovinum:upgrade-nudge", {
          detail: { reason: "low_credits", featureKey: detail?.feature || null },
        }));
      } else if (status === 403 && typeof detail === "string" && /pro|amateur|abonnement|premium/i.test(detail)) {
        window.dispatchEvent(new CustomEvent("geovinum:upgrade-nudge", {
          detail: { reason: "locked", featureKey: null, message: detail },
        }));
      } else if (status === 429) {
        window.dispatchEvent(new CustomEvent("geovinum:upgrade-nudge", {
          detail: { reason: "rate_limited", featureKey: null },
        }));
      }
    }
    return Promise.reject(err);
  }
);

export async function streamChat({ message, sessionId, lang, onSession, onDelta, onDone, onError, onAppellations }) {
  const token = auth.getToken();
  try {
    const res = await fetch(`${API}/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, session_id: sessionId, lang }),
    });
    if (!res.ok || !res.body) throw new Error("Stream init failed");
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const lines = part.split("\n");
        let event = "message";
        let dataLines = [];
        for (const line of lines) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
        }
        const data = dataLines.join("\n");
        if (event === "session") onSession?.(data);
        else if (event === "appellations") {
          try { onAppellations?.(JSON.parse(data)); } catch { /* ignore */ }
        }
        else if (event === "done") onDone?.();
        else if (data) {
          try {
            const parsed = JSON.parse(data);
            onDelta?.(parsed);
          } catch {
            onDelta?.(data);
          }
        }
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e);
  }
}


// Build WS url from REACT_APP_BACKEND_URL (replace http -> ws)
export function wsUrl(path) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
  return `${base}/api${path}`;
}
